/**
 * Stairs generator — a straight flight of steps built from box geometry.
 *
 * Coordinate convention: Z-up, 1 unit = 1 metre.
 *   - X = step width (across the staircase)
 *   - Y = run direction (stair advances in +Y)
 *   - Z = rise direction (stair ascends in +Z)
 *
 * Each step is two boxes:
 *   - Tread: the horizontal walking surface (width × run × tread thickness)
 *   - Riser: the vertical face between consecutive steps (width × riser
 *     thickness × rise)
 *
 * The generated geometry is centred horizontally (X) and starts at Z=0 /
 * Y=0 so the bottom front edge of the first riser sits at the local origin.
 * The top face of the highest step lands at Z = steps × rise.
 *
 * Optional railings are not geometry — they are described by the param schema
 * but left for a future revision that could spawn companion objects.
 */

import type { BoxSpec } from "../generators/generatorGeom.ts";
import {
  buildBox,
  concatBoxes,
  concatPrimitiveGeometries,
  transformGeometry,
} from "../generators/generatorGeom.ts";
import type { PrimitiveGeometry } from "../primitives.ts";
import type {
  Generator,
  GeneratorBuildContext,
  GeneratorOutput,
  GeneratorParamSpec,
  GeneratorParams,
} from "./types.ts";
import {
  boolParam,
  defineGenerator,
  intParam,
  numParam,
  paramsFromSpecs,
} from "./types.ts";

// ---------------------------------------------------------------------------
// Params
// ---------------------------------------------------------------------------

interface StairsParams extends GeneratorParams {
  stepCount: number;
  stepWidth: number;
  riseHeight: number;
  runDepth: number;
  treadThickness: number;
  riserThickness: number;
  hasLeftRail: boolean;
  hasRightRail: boolean;
  railHeight: number;
  railThickness: number;
  hasStringers: boolean;
  stringerThickness: number;
}

const STAIRS_SPECS: readonly GeneratorParamSpec[] = [
  {
    type: "section",
    id: "stairs-dimensions",
    label: "Dimensions",
    params: [
      {
        id: "stepCount",
        label: "Steps",
        type: "number",
        unit: "count",
        default: 10,
        min: 1,
        max: 40,
        step: 1,
      },
      {
        id: "stepWidth",
        label: "Width",
        type: "number",
        unit: "meters",
        default: 1.0,
        min: 0.5,
        max: 4,
        step: 0.05,
      },
      {
        id: "riseHeight",
        label: "Rise",
        type: "number",
        unit: "meters",
        default: 0.17,
        min: 0.08,
        max: 0.3,
        step: 0.005,
      },
      {
        id: "runDepth",
        label: "Run",
        type: "number",
        unit: "meters",
        default: 0.28,
        min: 0.15,
        max: 0.5,
        step: 0.005,
      },
    ],
  },
  {
    type: "section",
    id: "stairs-geometry",
    label: "Geometry",
    defaultCollapsed: true,
    params: [
      {
        id: "treadThickness",
        label: "Tread Thickness",
        type: "number",
        unit: "meters",
        default: 0.04,
        min: 0.01,
        max: 0.1,
        step: 0.005,
      },
      {
        id: "riserThickness",
        label: "Riser Thickness",
        type: "number",
        unit: "meters",
        default: 0.02,
        min: 0.01,
        max: 0.05,
        step: 0.005,
      },
      {
        id: "hasStringers",
        label: "Stringers",
        type: "boolean",
        default: true,
      },
      {
        id: "stringerThickness",
        label: "Stringer Thickness",
        type: "number",
        unit: "meters",
        default: 0.04,
        min: 0.01,
        max: 0.1,
        step: 0.005,
        showIf: { id: "hasStringers", value: true },
      },
    ],
  },
  {
    type: "section",
    id: "stairs-railing",
    label: "Railing",
    defaultCollapsed: true,
    params: [
      {
        id: "hasLeftRail",
        label: "Left Rail",
        type: "boolean",
        default: false,
      },
      {
        id: "hasRightRail",
        label: "Right Rail",
        type: "boolean",
        default: false,
      },
      {
        id: "railHeight",
        label: "Rail Height",
        type: "number",
        unit: "meters",
        default: 0.9,
        min: 0.6,
        max: 1.2,
        step: 0.05,
        showIf: { id: "hasLeftRail", value: true },
      },
      {
        id: "railThickness",
        label: "Rail Thickness",
        type: "number",
        unit: "meters",
        default: 0.04,
        min: 0.02,
        max: 0.1,
        step: 0.005,
        showIf: { id: "hasLeftRail", value: true },
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Geometry
// ---------------------------------------------------------------------------

function buildStairs(
  p: StairsParams,
  _ctx: GeneratorBuildContext,
): GeneratorOutput {
  const stepCount = intParam(p, "stepCount", 10);
  const stepWidth = numParam(p, "stepWidth", 1.0);
  const rise = numParam(p, "riseHeight", 0.17);
  const run = numParam(p, "runDepth", 0.28);
  const treadT = numParam(p, "treadThickness", 0.04);
  const riserT = numParam(p, "riserThickness", 0.02);
  const hasStringers = boolParam(p, "hasStringers", true);
  const stringerT = numParam(p, "stringerThickness", 0.04);
  const hasLeftRail = boolParam(p, "hasLeftRail", false);
  const hasRightRail = boolParam(p, "hasRightRail", false);
  const railHeight = numParam(p, "railHeight", 0.9);
  const railThick = numParam(p, "railThickness", 0.04);

  const boxes: BoxSpec[] = [];
  const extraGeometry: PrimitiveGeometry[] = [];

  // Total flight extents (used for centering)
  const totalRun = stepCount * run;
  const totalRise = stepCount * rise;

  // Steps: each step i (0-indexed) has its front edge at y = i * run,
  // and its top surface at z = (i + 1) * rise.
  for (let i = 0; i < stepCount; i++) {
    const frontY = i * run;
    const bottomZ = i * rise;
    const topZ = (i + 1) * rise;
    const treadDepth = Math.max(0.01, run - riserT);

    // Tread sits behind the riser so both thickness controls affect the
    // visible stair profile instead of just hidden overlaps.
    boxes.push({
      cx: 0,
      cy: frontY + riserT + treadDepth * 0.5,
      cz: topZ - treadT * 0.5,
      sx: stepWidth,
      sy: treadDepth,
      sz: treadT,
    });

    // Riser runs from the previous step surface to the underside of the tread.
    const riserH = Math.max(0.01, rise - treadT);
    boxes.push({
      cx: 0,
      cy: frontY + riserT * 0.5,
      cz: bottomZ + riserH * 0.5,
      sx: stepWidth,
      sy: riserT,
      sz: riserH,
    });
  }

  // Stringers — side supports running the full diagonal length of the flight
  if (hasStringers) {
    // Each stringer is approximated as a solid box that fills the triangular
    // area under the flight. We use one box per step at the correct height so
    // the underside follows the stair profile.
    for (let i = 0; i < stepCount; i++) {
      const frontY = i * run;
      const topZ = (i + 1) * rise;

      // Left stringer slice
      boxes.push({
        cx: -(stepWidth * 0.5 + stringerT * 0.5),
        cy: frontY + run * 0.5,
        cz: topZ * 0.5,
        sx: stringerT,
        sy: run,
        sz: topZ,
      });

      // Right stringer slice
      boxes.push({
        cx: stepWidth * 0.5 + stringerT * 0.5,
        cy: frontY + run * 0.5,
        cz: topZ * 0.5,
        sx: stringerT,
        sy: run,
        sz: topZ,
      });
    }
  }

  // Railings — simple vertical posts + a top rail along the flight diagonal
  if (hasLeftRail || hasRightRail) {
    const postW = railThick;
    // Post at every step nosing
    for (let i = 0; i <= stepCount; i++) {
      const py = i * run;
      const pz = i * rise + railHeight * 0.5;
      if (hasLeftRail) {
        boxes.push({
          cx: -(stepWidth * 0.5 - railThick * 0.5),
          cy: py,
          cz: pz,
          sx: postW,
          sy: postW,
          sz: railHeight,
        });
      }
      if (hasRightRail) {
        boxes.push({
          cx: stepWidth * 0.5 - railThick * 0.5,
          cy: py,
          cz: pz,
          sx: postW,
          sy: postW,
          sz: railHeight,
        });
      }
    }
    // Sloped top rail aligned to the stair pitch.
    const railLen = Math.hypot(totalRun, totalRise);
    const railAngle = Math.atan2(totalRise, totalRun);
    const railGeo = transformGeometry(
      buildBox(railThick, railLen, railThick),
      [0, totalRun * 0.5, totalRise * 0.5 + railHeight - railThick * 0.5],
      [-railAngle, 0, 0],
    );
    if (hasLeftRail) {
      extraGeometry.push(
        transformGeometry(railGeo, [
          -(stepWidth * 0.5 - railThick * 0.5),
          0,
          0,
        ]),
      );
    }
    if (hasRightRail) {
      extraGeometry.push(
        transformGeometry(railGeo, [stepWidth * 0.5 - railThick * 0.5, 0, 0]),
      );
    }
  }

  return {
    kind: "mesh",
    geometry: concatPrimitiveGeometries([concatBoxes(boxes), ...extraGeometry]),
  };
}

// ---------------------------------------------------------------------------
// Generator definition
// ---------------------------------------------------------------------------

export const STAIRS_GENERATOR: Generator = defineGenerator<StairsParams>({
  id: "stairs",
  label: "Stairs",
  category: "prop",
  paramSpecs: STAIRS_SPECS,
  defaultParams: paramsFromSpecs(STAIRS_SPECS) as StairsParams,
  build: buildStairs,
});
